<template>
  <div>
    <div
      v-if="!shouldHideTitle"
      class="image-title"
    >
      {{ title }}
    </div>
    <div
      ref="fileform"
      class="image-wrapper"
    >
      <Loader
        :isVisible="isLoading"
        class="assets-loader"
      />
      <div
        :class="imagePreviewClasses"
        :style="imagePreviewStyles"
      >
        <div
          :class="imageOverlayClasses"
        >
          <button
            v-if="source.canEdit && proccessStage === STAGES.READY"
            class="change-image-button"
            @click="handleChangeImageStart"
          >
            Change Image
          </button>
          <button
            v-if="source.canEdit && source.canDelete && proccessStage === STAGES.READY && preview"
            class="delete-image-button"
            @click="handleDeleteImage"
          >
            <inline-svg
              :src="iconRemoveImage"
              class="svg-icon"
            >
            </inline-svg>
          </button>
          <button
            v-if="proccessStage === STAGES.UPLOAD"
            class="change-image-button"
            @click="handleChangeImageCancel"
          >
            Cancel
          </button>

          <Button
            v-if="
              proccessStage === STAGES.UPLOAD_ERROR
                || proccessStage === STAGES.AFTER_LOAD_VALIDATION_ERROR"
            title="Clear"
            classType="warning"
            :eventFunction="handleChangeImageClear"
          />

          <Button
            v-if="proccessStage === STAGES.AFTER_LOAD_VALIDATION_SUCCESS"
            title="Cancel"
            classType="warning"
            :eventFunction="handleChangeImageCancel"
          />
          <Button
            v-if="proccessStage === STAGES.AFTER_LOAD_VALIDATION_SUCCESS"
            title="Apply"
            classType="primary"
            :eventFunction="handleChangeImageApply"
          />

          <Button
            v-if="proccessStage === STAGES.DELETE"
            title="Cancel"
            classType="warning"
            :eventFunction="handleDeleteImageCancel"
          />
          <Button
            v-if="proccessStage === STAGES.DELETE"
            title="Remove"
            classType="primary"
            :eventFunction="handleDeleteImageApply"
          />

          <div
            v-if="
              (proccessStage === STAGES.UPLOAD || proccessStage === STAGES.EMPTY)
                && source.canEdit"
            class="file-form"
            @click="onFileInput"
          >
            <div class="drag-zone">
              <inline-svg
                :src="iconUpload"
                class="svg"
              />
              <span class="drop-files">
                Drag & Drop
              </span>
              <span class="drop-files">
                or click to <span class="browser-link">browse</span>
              </span>
            </div>
          </div>
        </div>
        <img
          v-if="preview"
          ref="preview"
          :src="preview"
          :alt="fileName"
          :class="imageClasses"
          class="image-base-layer"
        />
        <img
          v-if="temporaryPreview"
          ref="temporaryPreview"
          :src="temporaryPreview"
          :alt="fileName"
          :class="imageClasses"
          class="image-overlay-layer"
        />
      </div>
      <input
        ref="fileInput"
        type="file"
        class="input-hidden"
        :accept="allowedFormatsText"
        @change="fileInputChange"
      />
      <span
        v-if="
          proccessStage === STAGES.AFTER_LOAD_VALIDATION_ERROR
            || proccessStage === STAGES.UPLOAD_ERROR
        "
        class="error-label"
      >
        {{ validationMessage }}
      </span>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import iconUpload from '../../../assets/icons/icon-upload.svg';
import iconRemoveImage from '../../../assets/icons/icon-remove-image.svg';
import Button from '../Button.vue';
import Loader from '../Loader.vue';
import CONFIG from '../../../constants/config';
import {
  validateImageFile,
  afterLoadValidationFunctionMap,
  imageConstraints,
} from '../../../utils/validation/imagesValidation';

export default {
  name: 'CollectionImageUploader',
  components: {
    Button,
    Loader,
  },
  props: {
    shouldHideTitle: {
      type: Boolean,
      default: false,
    },
    source: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    submitEventName: {
      type: String,
      default: '',
      required: true,
    },
    submitParams: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    shouldShowWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconUpload,
      iconRemoveImage,
      dragAndDropCapable: false,
      file: null,
      preview: '',
      temporaryPreview: '',
      validationMessage: '',
      fileName: '',
      proccessStage: '',
      STAGES: {
        EMPTY: 'empty',
        READY: 'ready',
        DELETE: 'delete',
        UPLOAD: 'upload',
        UPLOAD_ERROR: 'uploadError',
        AFTER_LOAD_VALIDATION: 'afterLoadValidation',
        AFTER_LOAD_VALIDATION_SUCCESS: 'afterLoadValidationSuccess',
        AFTER_LOAD_VALIDATION_ERROR: 'afterLoadValidationError',
        DONE: 'done',
        ERROR: 'error',
      },
      hasChanges: false,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters([
      'getCurrentAccountId',
    ]),
    sourceRatio() {
      const ratio = imageConstraints[this.source.imageType]?.sourceRatio || 1;

      return ratio;
    },
    imageConstants() {
      return imageConstraints[this.source.imageType] || {};
    },
    isSourceVertical() {
      return this.imageConstants?.sourceRatio < 1 || false;
    },
    isSourceHorizontal() {
      return this.imageConstants?.sourceRatio > 1 || false;
    },
    isImageSquare() {
      return this.imageConstants?.sourceRatio === 1 || false;
    },
    title() {
      const titlefromConstants = this.imageConstants?.title;
      return (
        titlefromConstants
        || 'Image'
      );
    },
    imagePreviewClasses() {
      return {
        'image-preview': true,
        'image-preview-error': this.proccessStage === this.STAGES.UPLOAD_ERROR || this.proccessStage === this.STAGES.AFTER_LOAD_VALIDATION_ERROR,
        'image-preview-warning': this.hasChanges && this.shouldShowWarning,
      };
    },
    imagePreviewStyles() {
      return {
        width: this.imageConstants?.wrapperWidth || '460px',
        height: this.imageConstants?.wrapperHeight || '390px',
        aspectRatio: this.imageConstants?.sourceRatio || 1,
      };
    },
    imageClasses() {
      return {
        image: true,
        'image-horizontal': this.isSourceHorizontal,
        'image-vertical': this.isSourceVertical,
        'image-square': this.isImageSquare,
      };
    },
    imageOverlayClasses() {
      return {
        'image-overlay': true,
        'image-overlay-changing': this.proccessStage === this.STAGES.UPLOAD
          || this.proccessStage === this.STAGES.AFTER_LOAD_VALIDATION
          || this.proccessStage === this.STAGES.AFTER_LOAD_VALIDATION_ERROR,
        'image-overlay-valid': this.proccessStage === this.STAGES.AFTER_LOAD_VALIDATION_SUCCESS,
        'image-overlay-delete': this.proccessStage === this.STAGES.DELETE,
      };
    },
    allowedFormatsText() {
      const formats = this.imageConstants?.allowedFormats || ['png', 'jpeg', 'jpg'];
      if (formats.length) {
        return formats.map((format) => `image/${format === 'png' ? 'x-png' : format}`).join(',');
      }
      return 'image/x-png,image/jpeg';
    },
  },
  watch: {
    hasChanges(newVal) {
      if (newVal) {
        this.$emit('handle-add-item-with-changes', this.source.id);
      } else {
        this.$emit('handle-remove-item-with-changes', this.source.id);
      }
    },
  },
  mounted() {
    this.proccessStage = this.STAGES.READY;
    this.dragInit();
    this.preview = this.source.url || '';
    this.file = this.source.url || '';
    this.fileName = this.source.name || '';

    if (!this.preview) {
      this.proccessStage = this.STAGES.EMPTY;
    }
  },
  methods: {
    startProcessing() {
      this.isLoading = true;
      this.$emit('handle-item-in-progress', this.source.id);
    },
    stopProcessing() {
      this.isLoading = false;
      this.$emit('handle-item-in-progress', null);
    },
    dragInit() {
      this.dragAndDropCapable = this.determineDragAndDropCapable();
      if (this.dragAndDropCapable) {
        ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
          this.$refs.fileform?.addEventListener(evt, (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, false);
        });

        this.$refs.fileform?.addEventListener('drop', this.dropHandle);
      }
    },
    dropHandle(e) {
      this.file = e.dataTransfer.files[0];
      this.fileName = this.file.name;
      this.getImagePreviews(this.file);
    },
    determineDragAndDropCapable() {
      const div = document.createElement('div');
      return (('draggable' in div)
        || ('ondragstart' in div && 'ondrop' in div))
        && 'FormData' in window
        && 'FileReader' in window;
    },
    getImagePreviews(file) {
      const validationResult = validateImageFile(file, imageConstraints[this.source.imageType]);
      if (!validationResult.isValid) {
        this.validationMessage = validationResult.validationMessage;
        this.proccessStage = this.STAGES.UPLOAD_ERROR;
        return;
      }

      this.proccessStage = this.STAGES.AFTER_LOAD_VALIDATION;
      this.validationMessage = '';
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const afterLoadValidation = afterLoadValidationFunctionMap[this.source.imageType];
        afterLoadValidation(reader.result).then((result) => {
          const { isValid } = result;
          if (isValid) {
            this.hasChanges = true;
            this.temporaryPreview = reader.result;
            this.proccessStage = this.STAGES.AFTER_LOAD_VALIDATION_SUCCESS;
          } else {
            this.validationMessage = result.validationMessage;
            this.proccessStage = this.STAGES.AFTER_LOAD_VALIDATION_ERROR;
          }
        }).catch(() => {
          this.proccessStage = this.STAGES.AFTER_LOAD_VALIDATION_ERROR;
        });
      }, false);

      reader.readAsDataURL(file);
    },
    fileInputChange() {
      const { files } = this.$refs.fileInput;
      if (files[0]) {
        this.file = files[0];
        this.fileName = this.file.name;
        this.getImagePreviews(this.file);
      }
    },
    onFileInput() {
      this.$refs.fileInput.click();
    },
    handleDeleteImage() {
      this.hasChanges = true;
      this.proccessStage = this.STAGES.DELETE;
    },
    handleChangeImageStart() {
      this.proccessStage = this.STAGES.UPLOAD;
    },
    handleChangeImageCancel() {
      this.startProcessing();
      // Очищаем временное изображение
      this.temporaryPreview = '';
      if (!this.source.url) {
        this.proccessStage = this.STAGES.EMPTY;
      } else {
        this.proccessStage = this.STAGES.READY;
      }
      this.$nextTick(() => {
        this.resetForm();
      });
    },
    handleChangeImageClear() {
      if (!this.source.url) {
        this.proccessStage = this.STAGES.EMPTY;
      } else {
        this.proccessStage = this.STAGES.READY;
      }
      this.resetForm();
    },
    handleDeleteImageCancel() {
      this.proccessStage = this.STAGES.READY;
      this.resetForm();
    },
    handleDeleteImageApply() {
      this.startProcessing();
      this.$refs.fileInput.value = '';
      this.file = null;
      this.preview = null;
      this.validationMessage = '';
      this.hasChanges = false;
      const params = {
        isSilent: true,
        ...this.submitParams,
      };

      const { imageProperty } = this.source;
      params.data[imageProperty] = null;

      this.$store.dispatch(this.submitEventName, params)
        .then(() => {
          this.$toasted.global.success({
            message: 'The Image has been successfully deleted',
          });

          this.$emit('handle-reload-item');
          this.proccessStage = this.STAGES.EMPTY;
          this.hasChanges = false;
        })
        .catch((error) => {
          this.onSubmitFail(error);
          this.proccessStage = this.STAGES.READY;
          this.hasChanges = false;
        })
        .finally(() => {
          this.stopProcessing();
        });
    },
    handleChangeImageApply() {
      this.startProcessing();
      const params = {
        isSilent: true,
        ...this.submitParams,
      };

      const { imageProperty } = this.source;
      params.data[imageProperty] = this.temporaryPreview;

      this.$store.dispatch(this.submitEventName, params)
        .then(() => {
          this.$toasted.global.success({
            message: 'The Image has been successfully updated',
          });

          this.preview = this.temporaryPreview;
          this.temporaryPreview = '';

          this.$emit('handle-reload-item');
          this.proccessStage = this.STAGES.READY;
          this.hasChanges = false;
        })
        .catch((error) => {
          this.onSubmitFail(error);
          this.proccessStage = this.STAGES.READY;
          this.hasChanges = false;
        })
        .finally(() => {
          this.stopProcessing();
        });
    },
    resetForm() {
      this.$refs.fileInput.value = '';
      this.file = null;
      this.preview = this.source.url || '';
      this.temporaryPreview = '';
      this.validationMessage = '';
      this.hasChanges = false;
      this.$emit('reset-should-show-warning');
      this.$nextTick(() => {
        this.stopProcessing();
      });
    },
    onSubmitFail(error) {
      const imgError = error
        && error.form_errors
        && error.form_errors.children
        && error.form_errors.children.posterImage
        && error.form_errors.children.posterImage.errors
        && error.form_errors.children.posterImage.errors[0].error;

      if (imgError === 'INVALID_MIME_TYPE_ERROR') {
        this.validationMessage = 'File MIME type should be PNG or JPEG';
      }

      this.$toasted.global.error({
        message: CONFIG.errorMessages.commonServerError,
      });
      this.resetForm();
    },
  },
};
</script>
<style scoped>
  .image-title {
    font-size: 16px;
    line-height: 20px;
    font-weight: 400;
    color: #ffffff;
    margin-bottom: 10px;
  }

  .image-wrapper {
    width: fit-content;
    height: auto;
    position: relative;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 3;
    transition: background-color 0.3s ease;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }

  .image-overlay-changing {
    background-color: rgba(0, 0, 0, 0.85);
  }

  .image-overlay-valid {
    background-color: rgba(0, 0, 0, 0.5);
  }

    .image-overlay-delete {
      background-color: rgba(0, 0, 0, 0.5);
    }

  .change-image-button {
    position: absolute;
    top: 0px;
    left: 0px;
    height: 40px;
    border: none;
    cursor: pointer;
    color: #ffffff;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    z-index: 4;
    background-color: rgba(0, 0, 0, 0.4);
    padding: 10px 16px;
    border-radius: 0 0 4px 0;
    transition: background-color 0.3s ease;
  }

  .change-image-button:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }

  .delete-image-button {
    position: absolute;
    top: 0;
    right: 0;
    height: 0px;
    width: 0px;
    border: none;
    cursor: pointer;
    color: #FB3951;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    z-index: 4;
    background-color: #383B40;
    padding: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0 0 0 4px;
    transition: all 0.3s ease-in-out 0.2s;
    opacity: 0;
  }

  .delete-image-button:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }

  .image-overlay:hover .delete-image-button {
    opacity: 1;
    height: 40px;
    width: 40px;
  }

  .delete-image-button .svg-icon ::v-deep path {
    fill: currentColor;
  }

  .image-preview {
    width: fit-content;
    height: fit-content;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    border: 1px dashed #a0a0a0;
  }

  .image-preview-error {
    border-color: #fb3951;
  }

  .image-preview-warning {
    border-color: #F2C94C;
  }

  .image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }

  .image-base-layer {
    position: relative;
    z-index: 0;
  }

  .image-overlay-layer {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .file-form {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    cursor: pointer;
  }

  .drag-zone {
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    align-items: center;
  }

  .browser-link {
    color: rgb(37, 145, 207);
  }

  .input-hidden {
    display: none;
  }
</style>
